import { useEffect, useState } from 'react';
import { MultiValue, SingleValue } from 'react-select';
import { SelectOptionType } from '@/components/element/Form/SelectOptionType';
import { SelectMulti } from '@/components/element/Form/SelectMulti';
import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { useWorkspaceUsers } from '@/hooks/api/0150_workspace_users';
import { useBusinessPartners } from '@/hooks/api/0430_business_partners';
import {
  PlDashboardSearchCondition,
  SalesOrdersDashboardSearchCondition,
  useDashboardSearchCondition,
} from '@/providers/DashboardSearchConditionProvider';
import { useProducts } from '@/hooks/api/0390_products';
import { useSalesContexts } from '@/hooks/api/2130_sales_contexts';
import { useMenuListedTeams } from '@/hooks/api/2260_menu_listed_teams';
import { SelectSingle } from '@/components/element/Form/SelectSingle';

export const FilterModal = () => {
  const { plDashboardSearchCondition: searchCondition, setPlDashboardSearchCondition: setSearchCondition } =
    useDashboardSearchCondition();

  const [condition, setCondition] = useState<PlDashboardSearchCondition>({ ...searchCondition });

  useEffect(() => setCondition({ ...searchCondition }), [searchCondition]);

  const onFiscalYearChange = (d: SingleValue<SelectOptionType>) => {
    setCondition((c) => ({ ...c, fiscal_year: d == null ? 0 : d.value }));
  };

  const onTeamsChange = (d: MultiValue<SelectOptionType>) => {
    setCondition((c) => ({ ...c, team_ids: d.map((a) => a.value) }));
  };

  const onUsersChange = (d: MultiValue<SelectOptionType>) => {
    setCondition((c) => ({ ...c, user_ids: d.map((a) => a.value) }));
  };

  const onUseExternalExpenseChange = () => {
    setCondition((current) => ({ ...current, use_external_expense: !current.use_external_expense }));
  };

  const onApplyClick = () => {
    setSearchCondition((_) => condition);
  };

  return (
    <div
      className="modal fade"
      id="modal-filter"
      tabIndex={-1}
      role="dialog"
      aria-labelledby="modal-form"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-centered modal-md" role="document">
        <div className="modal-content">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="exampleModalLabel">
                フィルタ
              </h5>
              <button type="button" className="btn-close text-dark" data-bs-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <form>
                <div className="form-group">
                  <label>年度</label>
                  <FiscalYearSelect onChange={onFiscalYearChange} selectedId={condition.fiscal_year} />
                </div>
                <div className="form-group">
                  <label>チーム</label>
                  <TeamsSelect onChange={onTeamsChange} selectedIds={condition.team_ids} />
                </div>
                <div className="form-group">
                  <label>担当者</label>
                  <UsersSelect onChange={onUsersChange} selectedIds={condition.user_ids} />
                </div>
                <div className="form-group">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="archived"
                      checked={condition.use_external_expense}
                      onChange={onUseExternalExpenseChange}
                    />
                    <label className="form-check-label" htmlFor="archived">
                      Node管理外費用を表示する
                    </label>
                  </div>
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">
                閉じる
              </button>
              <button type="button" className="btn bg-gradient-primary" data-bs-dismiss="modal" onClick={onApplyClick}>
                適用
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FiscalYearSelect = ({
  onChange,
  selectedId,
}: {
  onChange: (d: SingleValue<SelectOptionType>) => void;
  selectedId: number;
}) => {
  const [options, setOptions] = useState<SelectOptionType[]>([
    { label: '前年度', value: 1 },
    { label: '今年度', value: 0 },
    { label: '翌年度', value: 2 },
  ]);

  return <SelectSingle options={options} selectedId={selectedId} onChange={onChange} />;
};

const UsersSelect = ({
  onChange,
  selectedIds,
}: {
  onChange: (d: MultiValue<SelectOptionType>) => void;
  selectedIds: number[];
}) => {
  const { _w_ } = useCurrentWorkspace();
  const { data } = useWorkspaceUsers({ identifier: _w_!.identifier });
  const [options, setOptions] = useState<SelectOptionType[]>([]);

  useEffect(() => {
    if (data) setOptions(data.map((d): SelectOptionType => ({ value: d.id, label: d.user_account.name })));
  }, [data]);

  if (options == null) return <></>;

  return <SelectMulti options={options} selectedIds={selectedIds} onChange={onChange} />;
};

const TeamsSelect = ({
  onChange,
  selectedIds,
}: {
  onChange: (d: MultiValue<SelectOptionType>) => void;
  selectedIds: number[];
}) => {
  const { _w_ } = useCurrentWorkspace();
  const { data } = useMenuListedTeams({ identifier: _w_!.identifier });
  const [options, setOptions] = useState<SelectOptionType[]>([]);

  useEffect(() => {
    if (data) setOptions(data.sales_order_teams.map((d): SelectOptionType => ({ value: d.id, label: d.name })));
  }, [data]);

  if (options == null) return <></>;

  return <SelectMulti options={options} selectedIds={selectedIds} onChange={onChange} />;
};
